import React from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments } from '@modernloop/shared/components';

import { LeverHealthCheckBanner_LeverFragment } from 'src/generated/mloop-graphql';

import AppWideBanner, { AppWideBannerAction } from '../AppWideBanner';

interface Fragments {
  lever: LeverHealthCheckBanner_LeverFragment;
}

interface Props {
  retry: AppWideBannerAction;
}

const LeverHealthCheckBanner: FCWithFragments<Fragments, Props> = ({ lever, retry }) => {
  const { t } = useTranslation();
  // hide if its not installed or is healthy
  if (!lever || !lever?.isInstalled || lever?.isHealthy) {
    return null;
  }

  const endpoints = [
    !lever.canReadUsers && t('hiring-dashboard-nav:nav.banner.endpoint.users'),
    !lever.canReadJobs && t('hiring-dashboard-nav:nav.banner.endpoint.jobs'),
    !lever.canReadCandidates && t('hiring-dashboard-nav:nav.banner.endpoint.candidates'),
    !lever.canReadApplications && t('hiring-dashboard-nav:nav.banner.endpoint.applications'),
    !lever.canReadArchiveReasons && t('hiring-dashboard-nav:nav.banner.endpoint.archiveReasons'),
    !lever.canReadPanels && t('hiring-dashboard-nav:nav.banner.endpoint.panels'),
    !lever.canReadStages && t('hiring-dashboard-nav:nav.banner.endpoint.stages'),
    !lever.canReadDeletedCandidates && t('hiring-dashboard-nav:nav.banner.endpoint.deletedCandidates'),
  ].filter(Boolean) as string[];

  const text =
    endpoints.length > 0
      ? t('hiring-dashboard-nav:nav.banner.integrationUnhealthyWithEndpoints', {
          integrationName: 'Lever',
          endpoints,
        })
      : t('hiring-dashboard-nav:nav.banner.integrationUnhealthy', { integrationName: 'Lever' });

  return <AppWideBanner color="foreground" backgroundColor="error" text={text} action={retry} />;
};

LeverHealthCheckBanner.fragments = {
  lever: gql`
    fragment LeverHealthCheckBanner_lever on LeverIntegrationHealth {
      isHealthy
      isInstalled
      canReadUsers
      canReadJobs
      canReadCandidates
      canReadApplications
      canReadArchiveReasons
      canReadPanels
      canReadStages
      canReadDeletedCandidates
    }
  `,
};

export default LeverHealthCheckBanner;
